import { useTranslation } from 'react-i18next';
import { ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type StatusItem } from './types';

interface StatusItemCardProps {
  item: StatusItem;
  onClick: () => void;
}

// Normalise any incoming status value (upper/title/mixed case, spaces) to a
// canonical uppercase+underscore key so downstream maps match reliably.
function normalize(status: string): string {
  return status.trim().toUpperCase().replace(/\s+/g, '_');
}

// Canonical → translation key (camelCase slug under status.levels.*).
const STATUS_TO_KEY: Record<string, string> = {
  FULL: 'full',
  OK: 'ok',
  STOCKED: 'stocked',
  LOW: 'low',
  EMPTY: 'empty',
  WORKING: 'working',
  WEAK: 'weak',
  ISSUE: 'issue',
  NOT_WORKING: 'notWorking',
  BROKEN: 'broken',
  CLEAN: 'clean',
  NEEDS_ATTENTION: 'needsAttention',
  DIRTY: 'dirty',
  COMFORTABLE: 'comfortable',
  UNCOMFORTABLE: 'uncomfortable',
  BAD: 'bad',
};

type Variant = 'ok' | 'warning' | 'bad' | 'unknown';

// Canonical values that map to each variant. Anything unmapped reads as 'unknown'.
const GOOD = new Set(['FULL', 'OK', 'STOCKED', 'WORKING', 'CLEAN', 'COMFORTABLE']);
const WARN = new Set(['LOW', 'NEEDS_ATTENTION', 'ISSUE', 'WEAK']);
const BAD = new Set(['EMPTY', 'NOT_WORKING', 'BROKEN', 'DIRTY', 'UNCOMFORTABLE', 'BAD']);

function getStatusVariant(status: string): Variant {
  const k = normalize(status);
  if (GOOD.has(k)) return 'ok';
  if (WARN.has(k)) return 'warning';
  if (BAD.has(k)) return 'bad';
  return 'unknown';
}

// Human-readable fallback when a translation key is missing for a freeform
// status value (e.g. a tenant-curated tree node that the locale hasn't
// caught up to). "NEEDS_ATTENTION" → "Needs attention".
function humanize(status: string): string {
  return status
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()))
    .join(' ');
}

// Dot colour by variant. Matches the landing's accent system: green for OK,
// amber for warning, red for bad, neutral grey for unknown.
const DOT_CLASS: Record<Variant, string> = {
  ok: 'bg-[#10B981]',
  warning: 'bg-[#F59E0B]',
  bad: 'bg-[#DC2626] animate-pulse',
  // Darker than the #A3ADB8 used previously — #6B7280 hits 5.3:1 on white,
  // clearing WCAG AA for small text if the dot ever sits near muted copy.
  unknown: 'bg-[#6B7280]',
};

// Status label colour by variant.
const LABEL_CLASS: Record<Variant, string> = {
  ok: 'text-[#047857]',
  warning: 'text-[#B45309]',
  bad: 'text-[#B91C1C]',
  unknown: 'text-[#5A6472]',
};

/**
 * StatusItemCard, clean list row.
 *
 * Inside the disclosure on the v2 landing, status items read as a compact
 * list rather than a busy card grid. Left-aligned icon + name, right-aligned
 * status dot + localised label, everything tappable to open the modal.
 */
export function StatusItemCard({ item, onClick }: StatusItemCardProps) {
  const { t } = useTranslation('visitor');
  const variant = getStatusVariant(item.currentStatus);
  const canonical = normalize(item.currentStatus);
  const statusKey = STATUS_TO_KEY[canonical];
  const label = statusKey
    ? t(`status.levels.${statusKey}`, { defaultValue: humanize(item.currentStatus) })
    : humanize(item.currentStatus);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full min-h-[48px] flex items-center gap-3 rounded-[16px] border border-[#E8EAED] bg-white',
        'px-3.5 py-3 text-start transition-transform active:scale-[0.99]',
        // Guard hover styles behind a hover-capable media so touch devices
        // don't latch into a sticky hover fill after a tap.
        '[@media(hover:hover)]:hover:bg-[#FBFCFD]',
      )}
      aria-label={`${item.statusType.displayName ?? item.statusType.name}: ${label}. ${t('status.tapItemToUpdate')}`}
    >
      {/* Icon */}
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#F3F4F0]">
        {item.statusType.icon ? (
          <span className="text-[20px]" aria-hidden="true">
            {item.statusType.icon}
          </span>
        ) : (
          <ClipboardList className="h-4 w-4 text-[#5A6472]" aria-hidden="true" />
        )}
      </div>

      {/* Name, prefer the translated copy when the backend attached one.
          `dir="auto"` lets the browser pick the right bidi direction per
          string (translated name can land on the opposite-script page). */}
      <span
        className="flex-1 truncate text-[15px] font-medium text-[#0A0E14]"
        lang={item.statusType.nameLocale}
        dir="auto"
      >
        {item.statusType.displayName ?? item.statusType.name}
      </span>

      {/* Status (label + dot) */}
      <span className={cn('inline-flex items-center gap-2 text-[15px] font-medium', LABEL_CLASS[variant])}>
        {label}
        <span className={cn('h-[8px] w-[8px] rounded-full', DOT_CLASS[variant])} aria-hidden="true" />
      </span>
    </button>
  );
}
